import { useTranslation } from "react-i18next";
import Reveal from "@/components/base/Reveal";

const plans = [
  { key: 1, recommended: true, features: [1, 'support', 2, 3, 4, 5] },
  { key: 2, recommended: false, features: ['common1', 'common2', 2, 3, 4, 5, 6] },
];

export default function PricingPlans() {
  const { t } = useTranslation();

  return (
    <section id="plans" className="relative bg-midnight py-32 md:py-44">
      <div className="w-full px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="text-center mb-16 md:mb-24">
            <span className="inline-block px-4 py-1.5 border border-champagne/40 rounded-full text-champagne text-xs font-sans tracking-widest uppercase mb-6">
              {t("pricing.plans.tag")}
            </span>
            <h2 className="text-marble font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide">
              {t("pricing.plans.title")}
            </h2>
            <p className="text-marble/30 text-xs font-sans tracking-wide mt-3">
              {t("pricing.limit.note")}
            </p>
          </div>

          {/* --- Plan cards --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl p-8 md:p-10 transition-all duration-500 ${
                  plan.recommended
                    ? "border border-champagne/30 bg-gradient-to-b from-champagne/8 via-charcoal/30 to-charcoal/10 hover:-translate-y-2 hover:border-champagne/50 hover:shadow-[0_0_40px_-10px_rgba(212,175,55,0.15)]"
                    : "border border-white/5 bg-gradient-to-b from-white/[0.03] via-charcoal/10 to-transparent hover:-translate-y-2 hover:border-champagne/20 hover:shadow-[0_0_30px_-10px_rgba(212,175,55,0.08)]"
                }`}
              >
                {/* Decorative top gradient accent for recommended */}
                {plan.recommended && (
                  <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-champagne/50 to-transparent" />
                )}

                {plan.recommended && (
                  <span className="absolute -top-3.5 left-8 bg-champagne text-deepblack text-xs font-sans tracking-widest uppercase px-4 py-1 rounded-full shadow-[0_4px_12px_rgba(212,175,55,0.3)]">
                    {t(`pricing.plan${plan.key}.recommended`)}
                  </span>
                )}

                <div className="mb-6">
                  <h3 className="text-marble font-serif text-xl md:text-2xl font-light tracking-wide mb-2">
                    {t(`pricing.plan${plan.key}.name`)}
                  </h3>
                  <span className={`inline-block px-3 py-1 text-[10px] font-sans tracking-widest uppercase rounded-full ${
                    plan.recommended
                      ? "bg-champagne/15 text-champagne/90 border border-champagne/30"
                      : "bg-white/5 text-marble/50 border border-white/10"
                  }`}>
                    {t(`pricing.plan${plan.key}.tag`)}
                  </span>
                </div>

                <p className="text-marble/50 text-sm font-sans leading-relaxed mb-8 min-h-[60px]">
                  {t(`pricing.plan${plan.key}.desc`)}
                </p>

                <ul className="space-y-3.5 mb-10 pt-6 border-t border-white/5">
                  {plan.features.map((f) => {
                    const isNum = typeof f === 'number';
                    const i18nKey = isNum
                      ? `pricing.plan${plan.key}.f${f}`
                      : `pricing.plan${plan.key}.f_${f}`;
                    return (
                      <li key={String(f)} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          plan.recommended ? "bg-champagne/15" : "bg-white/5"
                        }`}>
                          <i className={`ri-check-line text-xs ${
                            plan.recommended ? "text-champagne" : "text-champagne/70"
                          }`} />
                        </div>
                        <span className="text-marble/70 text-sm font-sans leading-relaxed">
                          {t(i18nKey)}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* Review notes for plan1 */}
                <a
                  href="/inquiry"
                  className={`block text-center w-full py-3.5 rounded-full text-sm font-sans tracking-widest uppercase transition-all duration-300 ${
                    plan.recommended
                      ? "bg-champagne hover:bg-champagne/90 text-deepblack shadow-[0_4px_20px_rgba(212,175,55,0.25)] hover:shadow-[0_6px_30px_rgba(212,175,55,0.35)]"
                      : "bg-champagne/10 hover:bg-champagne/20 text-champagne border border-champagne/30 hover:border-champagne/50"
                  }`}
                >
                  {t(`pricing.plan${plan.key}.cta`)}
                </a>
              </div>
            ))}
          </div>

          {/* --- Disclaimer below both plan cards --- */}
          <div className="mt-10 max-w-4xl mx-auto">
            <div className="flex items-start gap-3 px-1">
              <i className="ri-information-line text-marble/25 text-sm mt-0.5 flex-shrink-0" />
              <div className="space-y-0.5">
                <p className="text-marble/25 text-xs font-sans leading-relaxed">
                  {t("pricing.plan1.noteReview")}
                </p>
                <p className="text-marble/25 text-xs font-sans leading-relaxed">
                  {t("pricing.plan1.noteInquiry")}
                </p>
              </div>
            </div>
          </div>

          {/* --- Follow-up info --- */}
          <div className="mt-20 md:mt-28 max-w-2xl mx-auto">
            <div className="relative rounded-2xl border border-champagne/30 bg-gradient-to-br from-champagne/8 via-champagne/5 to-transparent p-6 md:p-14 overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute -top-20 right-0 w-40 h-40 bg-champagne/10 rounded-full blur-[80px]" />

              <div className="relative z-10">
                {/* Section header with icon */}
                <div className="flex flex-col md:flex-row items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-champagne/15 flex items-center justify-center">
                    <i className="ri-shining-2-line text-champagne text-lg" />
                  </div>
                  <h3 className="text-marble font-serif text-sm md:text-xl lg:text-2xl font-light tracking-wide whitespace-nowrap text-center md:text-left">
                    {t("pricing.followup.title")}
                  </h3>
                </div>

                {/* Follow-up details */}
                <ul className="space-y-2 mb-4">
                  {[1, 2, 3, 4].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <i className="ri-check-line text-champagne mt-0.5 text-sm" />
                      <span className="text-marble/50 text-sm font-sans">
                        {t(`pricing.followup.b${b}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Follow-up features */}
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-champagne mt-0.5 text-sm" />
                    <span className="text-marble/70 text-sm font-sans">
                      {t("pricing.followup.f1")}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-champagne mt-0.5 text-sm" />
                    <span className="text-marble/70 text-sm font-sans">
                      {t("pricing.followup.f2")}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}